import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { HelperText, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import { ResumePersonalInfo } from '../../../types';
import { Palette, spacing, useThemePalette } from '../../../theme';
import PhotoPicker from '../PhotoPicker';

interface Props {
  value: ResumePersonalInfo;
  onChange: (next: ResumePersonalInfo) => void;
}

// Loose RFC-style check — good enough to catch typos like "foo@bar" or missing
// dot in domain. We deliberately don't pretend to enforce full RFC 5322.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (s: string) => !s || EMAIL_RE.test(s);

// Country code: optional "+", 1–4 digits. Empty is allowed.
const COUNTRY_CODE_RE = /^\+?\d{1,4}$/;
const isValidCountryCode = (s: string) => !s || COUNTRY_CODE_RE.test(s);

// Strip everything except digits. Used for the phone number itself.
const sanitizePhone = (s: string) => s.replace(/\D/g, '');
// Country code keeps an optional leading "+" plus digits.
const sanitizeCountryCode = (s: string) => {
  const cleaned = s.replace(/[^\d+]/g, '');
  // Allow only a single leading "+".
  return cleaned.replace(/(?!^)\+/g, '');
};

// Website: optional protocol, host with at least one dot, no whitespace.
// Matches "example.com", "https://example.com/path", "sub.example.co.uk".
const WEBSITE_RE = /^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/i;
const isValidWebsite = (s: string) => !s || WEBSITE_RE.test(s);

const PersonalInfoForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [emailTouched, setEmailTouched] = useState(false);
  const [countryTouched, setCountryTouched] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);

  const set = <K extends keyof ResumePersonalInfo>(
    k: K,
    v: ResumePersonalInfo[K]
  ) => onChange({ ...value, [k]: v });

  const emailError = emailTouched && !isValidEmail(value.email);
  const countryError =
    countryTouched && !isValidCountryCode(value.phoneCountryCode);
  const websiteError = websiteTouched && !isValidWebsite(value.website);

  return (
    <View style={styles.container}>
      <PhotoPicker
        value={value.photo}
        onChange={(v) => set('photo', v)}
      />
      <TextInput
        mode="outlined"
        label={t('resume.fields.fullName')}
        value={value.fullName}
        onChangeText={(v) => set('fullName', v)}
        autoCapitalize="words"
      />
      <TextInput
        mode="outlined"
        label={t('resume.fields.title')}
        value={value.title}
        onChangeText={(v) => set('title', v)}
      />
      <View>
        <TextInput
          mode="outlined"
          label={t('resume.fields.email')}
          value={value.email}
          onChangeText={(v) => set('email', v)}
          onBlur={() => setEmailTouched(true)}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          error={emailError}
        />
        {emailError && (
          <HelperText type="error" visible>
            {t('resume.validation.invalidEmail')}
          </HelperText>
        )}
      </View>
      <View style={styles.phoneRow}>
        <View style={styles.phoneCountry}>
          <TextInput
            mode="outlined"
            label={t('resume.fields.phoneCountryCode')}
            placeholder="+1"
            value={value.phoneCountryCode}
            onChangeText={(v) => set('phoneCountryCode', sanitizeCountryCode(v))}
            onBlur={() => setCountryTouched(true)}
            keyboardType="phone-pad"
            autoCorrect={false}
            error={countryError}
          />
        </View>
        <View style={styles.phoneNumber}>
          <TextInput
            mode="outlined"
            label={t('resume.fields.phone')}
            value={value.phone}
            onChangeText={(v) => set('phone', sanitizePhone(v))}
            keyboardType="number-pad"
            autoCorrect={false}
          />
        </View>
      </View>
      {countryError && (
        <HelperText type="error" visible style={styles.helperOffset}>
          {t('resume.validation.invalidCountryCode')}
        </HelperText>
      )}
      <TextInput
        mode="outlined"
        label={t('resume.fields.location')}
        value={value.location}
        onChangeText={(v) => set('location', v)}
      />
      <View>
        <TextInput
          mode="outlined"
          label={t('resume.fields.website')}
          value={value.website}
          onChangeText={(v) => set('website', v)}
          onBlur={() => setWebsiteTouched(true)}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="url"
          error={websiteError}
        />
        {websiteError && (
          <HelperText type="error" visible>
            {t('resume.validation.invalidWebsite')}
          </HelperText>
        )}
      </View>
      <TextInput
        mode="outlined"
        label={t('resume.fields.summary')}
        value={value.summary}
        onChangeText={(v) => set('summary', v)}
        multiline
        numberOfLines={4}
        style={styles.multiline}
      />
    </View>
  );
};

const makeStyles = (_p: Palette) =>
  StyleSheet.create({
    container: { gap: spacing.sm },
    multiline: { minHeight: 100 },
    phoneRow: { flexDirection: 'row', gap: spacing.sm },
    phoneCountry: { width: 110 },
    phoneNumber: { flex: 1 },
    helperOffset: { marginTop: -spacing.xs },
  });

export default PersonalInfoForm;
